import { db, withSyncWrite } from '../db/indexeddb'
import type { SyncQueueItem } from '../db/models'
import { getLastSupabaseError } from '../supabase/queries/logSupabaseError'
import { cloudTableOf, findOwnCloudRowByLocalId } from '../supabase/queries/localIdLookup'
import { markAsSynced, markAsFailed } from './sync-queue'
import type Dexie from 'dexie'

/**
 * **Der Insert traf die eigene, gelöschte Zeile** (23505 auf `local_id`, ADR 0035): Ein
 * wiederhergestelltes Backup bringt eine Zeile zurück, die das Gerät nach dem Sync weggeräumt hat —
 * die Cloud kennt sie noch als gelöscht. Dann die Cloud-Kennung übernehmen und SOFORT per Update
 * zurückholen (`deleted_at: null`): Ein bloß eingereihtes Update käme erst im nächsten Lauf, und
 * `purgeOrphanedCloudTags` räumte einen Tag im selben Lauf wieder weg (Diff-Audit 11.10.2026).
 *
 * Nie übernommen wird eine LEBENDE Cloud-Zeile — ihr Stand ist neuer als der eines Backups — und
 * keine, die schon eine andere lokale Zeile trägt; das wäre ein Doppel. `false` heißt: bleibt ein
 * Fehlversuch wie zuvor.
 */
async function adoptOwnCloudRow<TLocal>(change: any, entity: TLocal, userId: string, result: PushResult, descriptor: PushDescriptor<TLocal>): Promise<boolean> {
  if (getLastSupabaseError()?.code !== '23505') return false
  const localId = descriptor.getLocalId(entity)
  const cloud = await findOwnCloudRowByLocalId(cloudTableOf(descriptor.table.name), localId, userId)
  if (!cloud?.deleted) return false
  const twin = await descriptor.table.filter((row: { id: string; cloudId?: string | null }) => row.cloudId === cloud.cloudId && row.id !== localId).first()
  if (twin) return false

  await withSyncWrite(() => descriptor.table.update(localId, { cloudId: cloud.cloudId, userId }))
  await markAsSynced(change.id!)
  const adopted = await descriptor.getLocal(localId)
  const deferred = adopted && descriptor.canUpdate ? (await descriptor.canUpdate(adopted)).defer : false
  const updated = adopted && !deferred
    ? await descriptor.updateCloud(cloud.cloudId, await descriptor.buildUpdatePayload(adopted, change.data), userId)
    : null
  if (!updated) {
    await db.queueSyncChange('update', descriptor.table.name as SyncQueueItem['table'], { localId }, { silent: true })
    return true
  }
  await withSyncWrite(() => descriptor.table.update(localId, { synced: true, syncedAt: new Date(), updatedAt: new Date(updated.updated_at) }))
  result.updated++
  return true
}

/**
 * Generic Push Engine
 * Pushes local entity changes to cloud using a descriptor-based approach.
 */

/** Configuration for pushing a specific entity type to cloud. */
export interface PushDescriptor<TLocal> {
  entityName: string

  // Local DB
  getLocal: (localId: string) => Promise<TLocal | null | undefined>
  getLocalCloudId: (entity: TLocal) => string | null
  getLocalId: (entity: TLocal) => string

  // Payload builders
  buildCreatePayload: (entity: TLocal, userId: string) => Promise<Record<string, any>>
  /** `queued` ist, was beim Einreihen mitgegeben wurde (`queueSyncChange(…, data)`) — etwa, was sich geändert hat. */
  buildUpdatePayload: (entity: TLocal, queued?: Record<string, any>) => Promise<Record<string, any>>

  // Cloud operations
  createCloud: (userId: string, payload: any) => Promise<{ id: string; created_at: string } | null>
  updateCloud: (cloudId: string, payload: any, userId: string) => Promise<{ updated_at: string } | null>
  deleteCloud: (cloudId: string, userId: string) => Promise<boolean>

  // Dexie Table Reference
  table: Dexie.Table

  // Optional: Pre-push validation (e.g. "project not synced yet → defer")
  canCreate?: (entity: TLocal) => Promise<{ defer: boolean }>
  canUpdate?: (entity: TLocal) => Promise<{ defer: boolean }>
}

export interface PushResult {
  created: number
  updated: number
  deleted: number
  errors: string[]
}

/**
 * Push a single entity change to cloud.
 */
export async function pushEntityChange<TLocal>(
  change: any,
  userId: string,
  result: PushResult,
  descriptor: PushDescriptor<TLocal>,
): Promise<void> {
  switch (change.action) {
    case 'create': {
      const entity = await descriptor.getLocal(change.data?.localId ?? '')
      if (!entity) {
        // Entity was deleted locally — discard stale queue entry
        await markAsSynced(change.id!)
        break
      }

      // Skip if already linked to cloud (avoids duplicate push)
      if (descriptor.getLocalCloudId(entity)) {
        await markAsSynced(change.id!)
        break
      }

      // Check pre-push validation (e.g. parent project not synced yet)
      if (descriptor.canCreate) {
        const { defer } = await descriptor.canCreate(entity)
        if (defer) break
      }

      const payload = await descriptor.buildCreatePayload(entity, userId)
      const cloudResult = await descriptor.createCloud(userId, payload)

      if (cloudResult) {
        await withSyncWrite(() =>
          descriptor.table.update(descriptor.getLocalId(entity), {
            userId,
            cloudId: cloudResult.id,
            synced: true,
            syncedAt: new Date(),
            updatedAt: new Date(cloudResult.created_at),
          })
        )
        await markAsSynced(change.id!)
        result.created++
      } else if (!(await adoptOwnCloudRow(change, entity, userId, result, descriptor))) {
        await markAsFailed(change.id!, `create${descriptor.entityName} returned null`)
      }
      break
    }

    case 'update': {
      const entity = await descriptor.getLocal(change.data?.localId ?? '')
      if (!entity) {
        await markAsSynced(change.id!)
        break
      }

      const cloudId = descriptor.getLocalCloudId(entity)
      if (!cloudId) {
        // Entity hasn't been pushed to cloud yet — defer to next cycle
        break
      }

      // Check pre-push validation
      if (descriptor.canUpdate) {
        const { defer } = await descriptor.canUpdate(entity)
        if (defer) break
      }

      const payload = await descriptor.buildUpdatePayload(entity, change.data)
      console.log(`[Sync] PUSH ${descriptor.entityName} update: cloudId=${cloudId}`)
      const updateResult = await descriptor.updateCloud(cloudId, payload, userId)

      if (!updateResult) {
        result.errors.push(`update${descriptor.entityName} failed for ${cloudId}`)
        await markAsFailed(change.id!, `update${descriptor.entityName} returned null`)
        break
      }

      await withSyncWrite(() =>
        descriptor.table.update(descriptor.getLocalId(entity), {
          synced: true,
          syncedAt: new Date(),
          updatedAt: new Date(updateResult.updated_at),
        })
      )
      await markAsSynced(change.id!)
      result.updated++
      break
    }

    case 'delete': {
      const entity = await descriptor.getLocal(change.data?.localId ?? '')
      const cloudId = (entity ? descriptor.getLocalCloudId(entity) : null) ?? change.data?.cloudId
      if (cloudId) {
        const success = await descriptor.deleteCloud(cloudId, userId)
        if (!success) {
          result.errors.push(`delete${descriptor.entityName} failed for ${cloudId}`)
          await markAsFailed(change.id!, `delete${descriptor.entityName} returned false`)
          break
        }
        result.deleted++
      }
      // Mark as synced — if no cloudId, the entity was never uploaded
      await markAsSynced(change.id!)
      break
    }
  }
}
